import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

// ------------------------------
// Styled Components
// ------------------------------
const AsideShellContainer = styled.aside`
  background: #0f1622;
  border-left: 1px solid rgba(6, 182, 212, 0.3);
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease-in-out;
  ${props => props.$fullScreen ? `
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    max-width: none;
  ` : props.$wide ? `
    width: 100%;
    max-width: 72rem;
  ` : `
    width: 100%;
    max-width: 28rem;
  `}
`;

const Container = styled.div`
  position: relative;
  min-height: 100vh;
  background: #0b0e14;
  color: #e2e8f0;
`;

const AIButtonStyled = styled.button`
  position: fixed;
  right: 1rem;
  bottom: 1.5rem;
  z-index: 40;
  height: 3rem;
  padding: 0 1rem;
  border-radius: 9999px;
  background: #0f1622;
  border: 1px solid rgba(6, 182, 212, 0.4);
  color: #67e8f9;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(6, 182, 212, 0.6);
    background: #111827;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  display: flex;
`;

const OverlayBackdrop = styled.div`
  flex: 1;
  background: rgba(0, 0, 0, 0.6);
  cursor: pointer;
`;

const HeaderContainer = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #334155;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.span`
  color: #e2e8f0;
  font-size: 14px;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const HeaderButton = styled.button`
  font-size: 10px;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  border: 1px solid ${props => props.$primary ? 'rgba(6, 182, 212, 0.4)' : '#475569'};
  color: ${props => props.$primary ? '#67e8f9' : '#cbd5e1'};
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.$primary ? 'rgba(6, 182, 212, 0.6)' : '#64748b'};
  }
`;

const FlowStatusBarContainer = styled.div`
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #334155;
  font-size: 10px;
  display: flex;
  gap: 1.5rem;
  background: #0b1220;
`;

const FlowStatusItem = styled.div`
  color: ${props => props.$color || '#cbd5e1'};
  
  b {
    font-weight: 600;
  }
`;

const SellerAlertsContainer = styled.div`
  padding: 0.5rem 1rem;
  background: rgba(249, 115, 22, 0.1);
  border-bottom: 1px solid rgba(249, 115, 22, 0.3);
  font-size: 10px;
  color: #fdba74;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SellerAlertsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  color: #94a3b8;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
`;

const MessagesContainer = styled.div`
  padding: 1rem;
  font-size: 12px;
  overflow-y: auto;
  ${props => props.$showHistory ? 'height: 35%;' : 'height: 10%;'}
`;

const MessageText = styled.div`
  font-family: monospace;
  white-space: pre-line;
  color: #e2e8f0;
  margin-bottom: 0.5rem;
`;

const ContentContainer = styled.div`
  padding: 1rem;
  overflow-y: auto;
  ${props => props.$showHistory ? 'height: 55%;' : 'height: 80%;'}
`;

const FullScreenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  color: ${props => props.$color || '#67e8f9'};
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ScoreBadgeContainer = styled.div`
  font-size: 10px;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  border: 1px solid ${props => {
    if (props.$risk === 'LOW') return 'rgba(16, 185, 129, 0.4)';
    if (props.$risk === 'MEDIUM') return 'rgba(245, 158, 11, 0.4)';
    return 'rgba(244, 63, 94, 0.4)';
  }};
  color: ${props => {
    if (props.$risk === 'LOW') return '#6ee7b7';
    if (props.$risk === 'MEDIUM') return '#fbbf24';
    return '#fb7185';
  }};
`;

const InsightCard = styled.div`
  border-radius: 0.75rem;
  border: 1px solid rgba(6, 182, 212, 0.3);
  background: #0b1220;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InsightCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InsightCardTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #67e8f9;
`;

const InsightCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #e2e8f0;
`;

const InsightCardCTA = styled.div`
  display: flex;
  gap: 0.5rem;
  padding-top: 0.5rem;
`;

const CTAButton = styled.button`
  padding: 0.25rem 0.5rem;
  font-size: 10px;
  border-radius: 4px;
  background: rgba(6, 182, 212, 0.2);
  color: #67e8f9;
  border: 1px solid rgba(6, 182, 212, 0.3);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(6, 182, 212, 0.3);
    border-color: rgba(6, 182, 212, 0.5);
  }
`;

const WalletCard = styled.div`
  border-radius: 0.75rem;
  border: 1px solid rgba(16, 185, 129, 0.3);
  background: #0b1220;
  padding: 0.75rem;
`;

const WalletCardTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6ee7b7;
  margin-bottom: 0.5rem;
`;

const WalletCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #e2e8f0;
  margin-bottom: 0.25rem;
`;

const BuyerCard = styled.div`
  border-radius: 0.75rem;
  border: 1px solid rgba(168, 85, 247, 0.3);
  background: #0b1220;
  padding: 0.75rem;
`;

const BuyerCardTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #c084fc;
  margin-bottom: 0.5rem;
`;

const BuyerCardEmpty = styled.div`
  font-size: 12px;
  color: #94a3b8;
`;

const BuyerCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #e2e8f0;
  margin-bottom: 0.25rem;
`;

const SellerActionsCard = styled.div`
  border-radius: 0.75rem;
  border: 1px solid rgba(249, 115, 22, 0.3);
  background: #0b1220;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SellerActionsTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #fdba74;
`;

const SuggestionCard = styled.div`
  border: 1px solid ${props => props.$active ? 'rgba(16, 185, 129, 0.4)' : 'rgba(249, 115, 22, 0.2)'};
  background: ${props => props.$active ? 'rgba(16, 185, 129, 0.05)' : 'transparent'};
  border-radius: 0.5rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SuggestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #e2e8f0;
`;

const SuggestionProductName = styled.span`
  font-weight: 500;
`;

const SuggestionMeta = styled.div`
  font-size: 10px;
  color: #94a3b8;
`;

const SuggestionTiers = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  font-size: 10px;
  color: #e2e8f0;
`;

const SuggestionStatus = styled.div`
  margin-top: 0.25rem;
  width: 100%;
  text-align: center;
  font-size: 10px;
  padding: 0.25rem;
  border-radius: 4px;
  background: rgba(16, 185, 129, 0.2);
  color: #6ee7b7;
  border: 1px solid rgba(16, 185, 129, 0.3);
`;

const SuggestionButton = styled.button`
  margin-top: 0.25rem;
  width: 100%;
  font-size: 10px;
  padding: 0.25rem;
  border-radius: 4px;
  background: rgba(249, 115, 22, 0.2);
  color: #fdba74;
  border: 1px solid rgba(249, 115, 22, 0.3);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(249, 115, 22, 0.3);
    border-color: rgba(249, 115, 22, 0.5);
  }
`;

const BulkMatrixCard = styled.div`
  border-radius: 0.75rem;
  border: 1px solid rgba(71, 85, 105, 0.3);
  background: #0b1220;
  padding: 0.75rem;
`;

const BulkMatrixTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #cbd5e1;
  margin-bottom: 0.5rem;
`;

const BulkMatrixGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  font-size: 10px;
`;

const BulkMatrixHeader = styled.div`
  color: #94a3b8;
`;

const BulkMatrixCell = styled.div`
  color: #e2e8f0;
`;

const BudgetCard = styled.div`
  border-radius: 0.75rem;
  border: 1px solid rgba(6, 182, 212, 0.3);
  background: #0b1220;
  padding: 0.75rem;
`;

const BudgetTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #67e8f9;
  margin-bottom: 0.5rem;
`;

const BudgetRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${props => props.$highlight ? '#6ee7b7' : '#e2e8f0'};
  font-weight: ${props => props.$highlight ? '600' : 'normal'};
  margin-bottom: 0.25rem;
`;

const InputContainer = styled.div`
  padding: 0.75rem;
  border-top: 1px solid #334155;
`;

const TextArea = styled.textarea`
  width: 100%;
  resize: none;
  background: transparent;
  outline: none;
  font-family: monospace;
  font-size: 12px;
  max-height: 4rem;
  overflow-y: auto;
  color: #e2e8f0;
  border: none;

  &::placeholder {
    color: #64748b;
  }
`;

// ------------------------------
// AsideShell (layout safe)
// ------------------------------
function AsideShell({ fullScreen, wide, children }) {
  return (
    <AsideShellContainer $fullScreen={fullScreen} $wide={wide}>
      {children}
    </AsideShellContainer>
  );
}

// ======================================================
// Custodiy AI Agent Sidebar — V2.1 COMPLETE (STABLE)
// Includes:
// 1️⃣ Seller Actions
// 3️⃣ CTA on AI Cards
// 5️⃣ AI Scoring (confidence + risk)
// ======================================================

// ------------------------------
// Types & constants
// ------------------------------
const ROLES = { system: "system", user: "user", agent: "agent" };

// ------------------------------
// Mock APIs (replace in prod)
// ------------------------------
async function apiWalletStatus() {
  return { available: 42300, risk: "LOW" };
}

// ------------------------------
// ERP Adapter (mock)
// ------------------------------
const erpAdapter = {
  async getSupplierMetrics(product) {
    return { reliability: 0.9, compliance: 0.95 };
  },
};

// ------------------------------
// AI Command Router (ADVANCED)
// Modules: Forecast · Cluster · Anomaly · Governance · Budget
// ------------------------------
export async function routeAICommand(input, ctx) {
  const parts = input.trim().split(" ");
  const cmd = parts[0];

  switch (cmd) {
    case "/wallet": {
      const snap = await apiWalletStatus();
      return {
        type: "card",
        title: "Wallet Status",
        score: { confidence: 0.95, risk: snap.risk },
        rows: [
          { label: "Available", value: `EUR ${snap.available.toLocaleString()}` },
          { label: "Risk", value: snap.risk },
        ],
      };
    }

    case "/budget": {
      // /budget set 5000
      if (parts[1] === "set") {
        const value = Number(parts[2]);
        if (!value || value <= 0) {
          return { type: "text", text: "❌ Invalid budget value" };
        }

        return {
          type: "card",
          title: "Weekly Reorder Budget Set",
          score: { confidence: 0.9, risk: "LOW" },
          rows: [
            { label: "Pharmacist", value: ctx.userName },
            { label: "Weekly Budget", value: `€${value.toLocaleString()}` },
            { label: "Scope", value: "Warehouse replenishment" },
          ],
          cta: [{ label: "Confirm Auto-Reorder", action: "confirm-budget" }],
        };
      }
      return { type: "text", text: "Usage: /budget set <amount>" };
    }

    case "/forecast":
      return {
        type: "card",
        title: "Demand Forecast",
        score: { confidence: 0.81, risk: "MEDIUM" },
        cta: [{ label: "Create Auction", action: "create-auction" }],
        rows: [
          { label: "Product", value: parts[1] },
          { label: "Window", value: `${parts[2] || 14} days` },
          { label: "Forecast", value: "420 units" },
        ],
      };

    case "/supplier":
      if (parts[1] === "score") {
        const m = await erpAdapter.getSupplierMetrics(parts[2]);
        return {
          type: "card",
          title: "Supplier Score",
          score: { confidence: m.reliability, risk: m.reliability > 0.85 ? "LOW" : "MEDIUM" },
          rows: [
            { label: "Product", value: parts[2] },
            { label: "Reliability", value: `${Math.round(m.reliability * 100)}%` },
            { label: "Compliance", value: `${Math.round(m.compliance * 100)}%` },
          ],
        };
      }
      break;

    default:
      break;
  }

  return null;
}

// ------------------------------
// UI Cards
// ------------------------------
function ScoreBadge({ score }) {
  return (
    <ScoreBadgeContainer $risk={score.risk}>
      {Math.round(score.confidence * 100)}% · {score.risk}
    </ScoreBadgeContainer>
  );
}

function AIInsightCard({ title, rows, score, cta, onCTA }) {
  return (
    <InsightCard>
      <InsightCardHeader>
        <InsightCardTitle>{title}</InsightCardTitle>
        {score && <ScoreBadge score={score} />}
      </InsightCardHeader>
      {rows.map((r) => (
        <InsightCardRow key={r.label}>
          <span>{r.label}</span>
          <span>{r.value}</span>
        </InsightCardRow>
      ))}
      {cta && (
        <InsightCardCTA>
          {cta.map((c) => (
            <CTAButton
              key={c.action}
              onClick={() => onCTA?.(c.action)}
            >
              {c.label}
            </CTAButton>
          ))}
        </InsightCardCTA>
      )}
    </InsightCard>
  );
}

function AIWalletCard({ available, risk }) {
  return (
    <WalletCard>
      <WalletCardTitle>WALLET</WalletCardTitle>
      <WalletCardRow>
        <span>Available</span>
        <span>EUR {available.toLocaleString()}</span>
      </WalletCardRow>
      <WalletCardRow>
        <span>Risk</span>
        <span>{risk}</span>
      </WalletCardRow>
    </WalletCard>
  );
}

function AIBuyerCard({ aggregated }) {
  return (
    <BuyerCard>
      <BuyerCardTitle>BUYER SIGNAL</BuyerCardTitle>
      {Object.keys(aggregated).length === 0 && <BuyerCardEmpty>No demand yet</BuyerCardEmpty>}
      {Object.entries(aggregated).map(([p, q]) => (
        <BuyerCardRow key={p}>
          <span>{p}</span>
          <span>{q}</span>
        </BuyerCardRow>
      ))}
    </BuyerCard>
  );
}

function AISellerActions({ aggregated, activeAuctions, onCreateAuction }) {
  if (Object.keys(aggregated).length === 0) return null;

  const suggestions = Object.entries(aggregated).map(([product, qty]) => {
    const participants = Math.max(2, Math.ceil(qty / 50));
    const supplierRisk = activeAuctions[product] ? 0.95 : 1;
    const tier1 = qty >= 300 ? `€${(16.9 * supplierRisk).toFixed(2)}` : `€${(17.9 * supplierRisk).toFixed(2)}`;
    const tier2 = qty >= 300 ? `€${(16.3 * supplierRisk).toFixed(2)}` : `€${(17.1 * supplierRisk).toFixed(2)}`;
    const tier3 = qty >= 300 ? `€${(15.8 * supplierRisk).toFixed(2)}` : `€${(16.5 * supplierRisk).toFixed(2)}`;

    return {
      product,
      qty,
      participants,
      store: "Primary Pharma Store",
      tiers: [tier1, tier2, tier3],
    };
  });

  return (
    <SellerActionsCard>
      <SellerActionsTitle>AUCTION SUGGESTIONS</SellerActionsTitle>
      {suggestions.map((s) => (
        <SuggestionCard key={s.product} $active={!!activeAuctions[s.product]}>
          <SuggestionHeader>
            <SuggestionProductName>{s.product}</SuggestionProductName>
            <span>{s.qty} units</span>
          </SuggestionHeader>
          <SuggestionMeta>
            {s.participants} buyers · Store: {s.store}
          </SuggestionMeta>
          <SuggestionTiers>
            <div>Tier 1<br />{s.tiers[0]}</div>
            <div>Tier 2<br />{s.tiers[1]}</div>
            <div>Tier 3<br />{s.tiers[2]}</div>
          </SuggestionTiers>
          {activeAuctions[s.product] ? (
            <SuggestionStatus>
              Auction in progress
            </SuggestionStatus>
          ) : (
            <SuggestionButton onClick={() => onCreateAuction(s.product)}>
              Create Auction
            </SuggestionButton>
          )}
        </SuggestionCard>
      ))}
    </SellerActionsCard>
  );
}

// ------------------------------
// HIGH-VOLUME VISUALIZATION
// ------------------------------
function BulkDemandMatrix({ aggregated }) {
  if (Object.keys(aggregated).length < 4) return null;

  return (
    <BulkMatrixCard>
      <BulkMatrixTitle>DEMAND MATRIX (HIGH VOLUME)</BulkMatrixTitle>
      <BulkMatrixGrid>
        <BulkMatrixHeader>Product</BulkMatrixHeader>
        <BulkMatrixHeader>Total Qty</BulkMatrixHeader>
        <BulkMatrixHeader>Buyers</BulkMatrixHeader>
        {Object.entries(aggregated).map(([p, q]) => (
          <React.Fragment key={p}>
            <BulkMatrixCell>{p}</BulkMatrixCell>
            <BulkMatrixCell>{q}</BulkMatrixCell>
            <BulkMatrixCell>{Math.max(2, Math.ceil(q / 50))}</BulkMatrixCell>
          </React.Fragment>
        ))}
      </BulkMatrixGrid>
    </BulkMatrixCard>
  );
}

// ------------------------------
// FLOW STATUS BAR
// ------------------------------
function FlowStatusBar({
  buyerCount,
  sellerCount,
  buyerUpdatedAt,
  sellerUpdatedAt,
}) {
  const fmt = (ts) => (ts ? `${Math.max(1, Math.floor((Date.now() - ts) / 60000))}m ago` : "—");
  return (
    <FlowStatusBarContainer>
      <FlowStatusItem $color="#c084fc">
        BUYER FLOWS <b>{buyerCount}</b> · last {fmt(buyerUpdatedAt)}
      </FlowStatusItem>
      <FlowStatusItem $color="#fdba74">
        SELLER FLOWS <b>{sellerCount}</b> · last {fmt(sellerUpdatedAt)}
      </FlowStatusItem>
      <FlowStatusItem $color="#6ee7b7" style={{ marginLeft: 'auto' }}>
        ● LIVE
      </FlowStatusItem>
    </FlowStatusBarContainer>
  );
}

// ------------------------------
// MAIN COMPONENT
// ------------------------------
// ======================================================
// AI GOVERNANCE DASHBOARD (ADMIN / COMPLIANCE)
// Read-only · Audit-first · Pharma-ready
// ======================================================

const GovernanceContainer = styled.div`
  min-height: 100vh;
  background: #0b0e14;
  color: #e2e8f0;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const GovernanceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GovernanceTitle = styled.h1`
  font-size: 1.125rem;
  font-weight: 600;
  color: #67e8f9;
`;

const GovernanceSubtitle = styled.div`
  font-size: 12px;
  color: #94a3b8;
`;

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.75rem;
`;

const KPICard = styled.div`
  border-radius: 0.5rem;
  border: 1px solid rgba(6, 182, 212, 0.3);
  background: #0b1220;
  padding: 0.75rem;
`;

const KPILabel = styled.div`
  font-size: 10px;
  color: #94a3b8;
`;

const KPIValue = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #67e8f9;
`;

const TimelineCard = styled.div`
  border-radius: 0.75rem;
  border: 1px solid #334155;
  background: #0f1622;
`;

const TimelineHeader = styled.div`
  padding: 0.5rem 1rem;
  font-size: 12px;
  font-weight: 600;
  color: #cbd5e1;
  border-bottom: 1px solid #334155;
`;

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #334155;
`;

const TimelineRow = styled.div`
  padding: 0.5rem 1rem;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  font-size: 12px;
  color: #e2e8f0;
  border-bottom: 1px solid #334155;

  &:last-child {
    border-bottom: none;
  }
`;

const RiskCell = styled.div`
  color: ${props => {
    if (props.$risk === 'HIGH') return '#fb7185';
    if (props.$risk === 'MEDIUM') return '#fbbf24';
    return '#6ee7b7';
  }};
`;

const ControlsCard = styled.div`
  border-radius: 0.75rem;
  border: 1px solid #334155;
  background: #0f1622;
  padding: 1rem;
`;

const ControlsTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #cbd5e1;
  margin-bottom: 0.5rem;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 12px;
  color: #e2e8f0;
`;

const ControlsValue = styled.span`
  color: #6ee7b7;
`;

export function AIGovernanceDashboard() {
  const [stats] = useState({
    decisions: 312,
    avgConfidence: 0.78,
    highRisk: 9,
    overrides: 4,
    alerts: 2,
  });

  const decisions = [
    { id: 1, type: "Forecast", product: "Paracetamol", confidence: 0.81, risk: "MEDIUM", user: "Pharmacist" },
    { id: 2, type: "Supplier", product: "Ibuprofen", confidence: 0.92, risk: "LOW", user: "Reseller" },
  ];

  return (
    <GovernanceContainer>
      <GovernanceHeader>
        <GovernanceTitle>AI Governance Dashboard</GovernanceTitle>
        <GovernanceSubtitle>Model v2.1 · Status ENABLED</GovernanceSubtitle>
      </GovernanceHeader>

      <KPIGrid>
        <KPICard>
          <KPILabel>AI Decisions</KPILabel>
          <KPIValue>{stats.decisions}</KPIValue>
        </KPICard>
        <KPICard>
          <KPILabel>Avg Confidence</KPILabel>
          <KPIValue>{Math.round(stats.avgConfidence * 100)}%</KPIValue>
        </KPICard>
        <KPICard>
          <KPILabel>High Risk</KPILabel>
          <KPIValue>{stats.highRisk}</KPIValue>
        </KPICard>
        <KPICard>
          <KPILabel>Overrides</KPILabel>
          <KPIValue>{stats.overrides}</KPIValue>
        </KPICard>
        <KPICard>
          <KPILabel>Alerts</KPILabel>
          <KPIValue>{stats.alerts}</KPIValue>
        </KPICard>
      </KPIGrid>

      <TimelineCard>
        <TimelineHeader>AI Decisions Timeline</TimelineHeader>
        <TimelineList>
          {decisions.map((d) => (
            <TimelineRow key={d.id}>
              <div>{d.type}</div>
              <div>{d.product}</div>
              <div>{Math.round(d.confidence * 100)}%</div>
              <RiskCell $risk={d.risk}>{d.risk}</RiskCell>
              <div>{d.user}</div>
            </TimelineRow>
          ))}
        </TimelineList>
      </TimelineCard>

      <ControlsCard>
        <ControlsTitle>Governance Controls</ControlsTitle>
        <ControlsRow>
          <div>AI Engine: <ControlsValue>ENABLED</ControlsValue></div>
          <div>Confidence Threshold: 75%</div>
          <div>Human Override: ACTIVE</div>
        </ControlsRow>
      </ControlsCard>
    </GovernanceContainer>
  );
}

export default function CustodiyAIAgentSidebar({ open: openProp, onClose }) {
  const [showHistory, setShowHistory] = useState(true);
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setOpenInternal(false);
    }
  };
  const [fullScreen, setFullScreen] = useState(false);
  const [input, setInput] = useState("");

  const [userProfile] = useState({ name: "Giuseppe", role: "pharmacist" });

  const messagesEndRef = React.useRef(null);

  const [messages, setMessages] = useState([
    { role: ROLES.system, text: "Custodiy AI Agent online. Type /help", ts: Date.now() },
  ]);

  const [aiCards, setAICards] = useState([]);
  const [wallet, setWallet] = useState(null);

  // Weekly budget (pharmacist)
  const [weeklyBudget, setWeeklyBudget] = useState(null);
  const [budgetThreshold] = useState(0.2); // 20% threshold
  const [budgetSpent, setBudgetSpent] = useState(0);

  // Mock DB persistence
  async function persistBudgetToDB(amount) {
    console.log("DB → ai_budget", { amount, ts: new Date().toISOString() });
  }

  function getBudgetResidual() {
    if (!weeklyBudget) return null;
    return Math.max(weeklyBudget - budgetSpent, 0);
  }

  async function persistAutoBuyToDB(product, qty, source) {
    console.log("DB → ai_auto_buy", { product, qty, source, ts: new Date().toISOString() });
  }

  // Flow status
  const [buyerFlowAt, setBuyerFlowAt] = useState(null);
  const [sellerFlowAt, setSellerFlowAt] = useState(null);
  const [lastBudgetReset, setLastBudgetReset] = useState(null);
  const [sellerAlerts, setSellerAlerts] = useState([]);
  const [showSellerAlerts, setShowSellerAlerts] = useState(true);
  // Active auctions by product
  const [activeAuctions, setActiveAuctions] = useState({});

  const [memory, setMemory] = useState({ desired: [], urgent: [] });

  const buyerAggregated = useMemo(() => {
    const acc = {};
    [...memory.desired, ...memory.urgent].forEach(({ product, qty }) => {
      acc[product] = (acc[product] || 0) + qty;
    });
    return acc;
  }, [memory]);

  // Auto-update buyer flow timestamp
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

    // 📅 Weekly budget reset (every 7 days)
    if (weeklyBudget && lastBudgetReset) {
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - lastBudgetReset > sevenDays) {
        setBudgetSpent(0);
        setLastBudgetReset(Date.now());
        setMessages((m) => [...m, { role: ROLES.system, text: "🔄 Weekly budget reset", ts: Date.now() }]);
      }
    }
  }, [messages, weeklyBudget, lastBudgetReset]);

  async function route(cmd) {
    // ==============================
    // MASSIVE LOAD TEST COMMAND
    // ==============================
    if (cmd.startsWith("/loadtest")) {
      const [, buyersRaw, productsRaw] = cmd.split(" ");
      const buyers = Number(buyersRaw) || 50;
      const products = Number(productsRaw) || 20;

      const generated = [];

      for (let p = 1; p <= products; p++) {
        const productName = `Product-${p}`;
        const totalQty = Math.floor(Math.random() * buyers * 10) + buyers * 5;
        generated.push({ product: productName, qty: totalQty });
      }

      setMemory(() => ({
        desired: generated.map((g) => ({ product: g.product, qty: g.qty })),
        urgent: [],
      }));

      setMessages((m) => [
        ...m,
        {
          role: ROLES.agent,
          text: `🚀 Load test executed: ${buyers} buyers · ${products} products`,
          ts: Date.now(),
        },
      ]);
      return;
    }

    if (!cmd.trim()) return;

    // log user command
    setMessages((m) => [...m, { role: ROLES.user, text: cmd, ts: Date.now() }]);

    // AI advanced commands
    const ai = await routeAICommand(cmd, {
      userId: "local",
      userName: userProfile.name,
      userRole: userProfile.role,
      organizationId: "default",
      country: "IT",
    });

    if (ai && ai.type === "card") {
      setAICards((c) => [ai, ...c]);
      setSellerFlowAt(Date.now());
      const highOpportunity = ai.rows?.some(r => r.label === "Forecast" || r.label === "Quantity");
      if (highOpportunity) {
        setSellerAlerts(a => [`New seller opportunity detected · ${ai.title}`, ...a]);
        if (document.hidden) {
          try {
            new Notification("Custodiy · Seller Alert", { body: ai.title });
          } catch {}
        }
      }
      setMessages((m) => [...m, { role: ROLES.agent, text: `✔ Executed ${cmd}`, ts: Date.now() }]);
      return;
    }

    // Handle budget set command
    if (cmd.startsWith("/budget set")) {
      const parts = cmd.trim().split(" ");
      const value = Number(parts[2]);
      if (!value || value <= 0) {
        setMessages((m) => [...m, { role: ROLES.agent, text: "❌ Invalid budget value", ts: Date.now() }]);
        return;
      }
      setWeeklyBudget(value);
      setBudgetSpent(0);
      setLastBudgetReset(Date.now());
      await persistBudgetToDB(value);
      setMessages((m) => [...m, { role: ROLES.agent, text: `✅ Weekly budget set to €${value.toLocaleString()}`, ts: Date.now() }]);
      return;
    }

    // local commands
    const c = cmd.toLowerCase();

    if (c === "/wallet") {
      const snap = await apiWalletStatus();
      setWallet(snap);
      setMessages((m) => [...m, { role: ROLES.agent, text: `Wallet updated · EUR ${snap.available}`, ts: Date.now() }]);
      return;
    }

    if (c.startsWith("/buy urgent")) {
      const [, , p, q] = c.split(" ");
      setMemory((m) => ({ ...m, urgent: [...m.urgent, { product: p, qty: Number(q) || 1 }] }));
      setBuyerFlowAt(Date.now());
      if (activeAuctions[p]) {
        setMessages((m) => [...m, { role: ROLES.agent, text: `ℹ️ Added to active auction · ${p}`, ts: Date.now() }]);
      } else {
        setMessages((m) => [...m, { role: ROLES.agent, text: `Recorded URGENT demand · ${p}`, ts: Date.now() }]);
      }
      return;
    }

    if (c.startsWith("/buy ")) {
      const [, p, q] = c.split(" ");
      setMemory((m) => ({ ...m, desired: [...m.desired, { product: p, qty: Number(q) || 1 }] }));
      setBuyerFlowAt(Date.now());
      if (activeAuctions[p]) {
        setMessages((m) => [...m, { role: ROLES.agent, text: `ℹ️ Added to active auction · ${p}`, ts: Date.now() }]);
      } else {
        setMessages((m) => [...m, { role: ROLES.agent, text: `Recorded demand · ${p}`, ts: Date.now() }]);
      }
      return;
    }

    if (c.startsWith("/help")) {
      const [, topic] = c.split(" ");
      const helpMap = {
        supplier: `🏭 SUPPLIER COMMANDS

/supplier score <product>
Evaluates reliability and risk using ERP metrics.

/supplier compare <product>
Compares multiple suppliers.

/supplier blacklist <supplier>
Marks supplier as excluded.

/supplier explain <product>
Explains how the supplier score was calculated.`,
        buy: `🟣 BUY COMMANDS

/buy <product> <qty>
/buy urgent <product> <qty>
→ Demand is aggregated per product
→ If an auction is active, demand is added to it`,
        forecast: `📈 DEMAND FORECAST

/forecast <product> [days]
Predicts future demand with confidence`,
        reorder: `🔄 REORDER OPTIMIZATION

/reorder optimize <product>
Suggests optimal reorder quantity`,
        wallet: `💼 WALLET STATUS

/wallet
Shows available balance and risk`,
        budget: `💰 WEEKLY REORDER BUDGET

/budget set <amount>
Sets the weekly budget dedicated to warehouse replenishment.

How it works:
• Budget is stored for the pharmacist profile
• When stock is low and wallet is sufficient, AI suggests reorders
• On confirmation, automatic /buy orders are generated
• Budget is split across products based on aggregated demand`,
        loadtest: `🧪 LOAD TESTING

/loadtest <buyers> <products>
Stress test the system`,
      };

      const content = topic
        ? helpMap[topic] || "No help available for this topic."
        : `📖 GUIDED HELP — HOW TO USE CUSTODIY AI

1️⃣ BUYER DEMAND
/buy <product> <qty>
/buy urgent <product> <qty>

2️⃣ ANALYZE
/forecast <product>

3️⃣ SUPPLIERS
/supplier score | compare | explain

4️⃣ SELLER ACTIONS
Create Auction button

5️⃣ OPERATIONS
/reorder optimize
/wallet
/budget set <amount>

Type /help <topic> for details (e.g. /help budget).`;

      setMessages((m) => [...m, { role: ROLES.agent, text: content, ts: Date.now() }]);
      return;
    }

    // fallback
    setMessages((m) => [...m, { role: ROLES.agent, text: "Unknown command. Type /help", ts: Date.now() }]);
  }

  return (
    <>
      {openProp === undefined && (
        <AIButtonStyled onClick={() => setOpenInternal(true)}>
          AI
        </AIButtonStyled>
      )}

      {open && (
        <Overlay>
          <OverlayBackdrop onClick={handleClose} />
          <AsideShell fullScreen={fullScreen} wide={aiCards.length > 5}>
            <HeaderContainer>
              <HeaderTitle>Custodiy · AI Agent</HeaderTitle>
              <HeaderButtons>
                <HeaderButton $primary onClick={() => setFullScreen(v => !v)}>
                  {fullScreen ? "Exit Fullscreen" : "Fullscreen"}
                </HeaderButton>
                <HeaderButton onClick={() => setShowHistory(v => !v)}>
                  {showHistory ? "Hide history" : "Show history"}
                </HeaderButton>
              </HeaderButtons>
            </HeaderContainer>

            <FlowStatusBar
              buyerCount={Object.keys(buyerAggregated).length}
              sellerCount={aiCards.length}
              buyerUpdatedAt={buyerFlowAt}
              sellerUpdatedAt={sellerFlowAt}
            />

            {showSellerAlerts && sellerAlerts.length > 0 && (
              <SellerAlertsContainer>
                <SellerAlertsHeader>
                  <span>🔔 Seller notifications</span>
                  <CloseButton onClick={() => setShowSellerAlerts(false)}>✕</CloseButton>
                </SellerAlertsHeader>
                {sellerAlerts.slice(0, 3).map((a, i) => (
                  <div key={i}>• {a}</div>
                ))}
              </SellerAlertsContainer>
            )}

            <MessagesContainer $showHistory={showHistory}>
              {messages.filter((m) => Date.now() - m.ts < 1000 * 60 * 60 * 48).map((m, i) => (
                <MessageText key={i}>{m.text}</MessageText>
              ))}
              <div ref={messagesEndRef} />
            </MessagesContainer>

            <ContentContainer $showHistory={showHistory}>
              {fullScreen ? (
                <FullScreenGrid>
                  <SectionContainer>
                    <SectionTitle $color="#c084fc">BUYER INSIGHTS</SectionTitle>
                    <AIBuyerCard aggregated={buyerAggregated} />
                    <BulkDemandMatrix aggregated={buyerAggregated} />
                  </SectionContainer>
                  <SectionContainer>
                    <SectionTitle $color="#67e8f9">AI INSIGHTS</SectionTitle>
                    {aiCards.map((c, i) => c.type === "card" && (
                      <AIInsightCard
                        key={i}
                        {...c}
                        onCTA={async (action) => {
                          if (action === "confirm-budget" && weeklyBudget) {
                            let spentNow = 0;
                            const products = Object.entries(buyerAggregated);
                            let remaining = weeklyBudget;

                            for (const [product, qty] of products) {
                              if (remaining <= 0) break;
                              const buyQty = Math.min(qty, Math.floor(remaining / 20));
                              if (buyQty <= 0) continue;

                              setMemory(m => ({
                                ...m,
                                desired: [...m.desired, { product, qty: buyQty }]
                              }));

                              await persistAutoBuyToDB(product, buyQty, "weekly-budget");
                              spentNow += buyQty * 20;
                              remaining -= buyQty * 20;
                            }

                            setBudgetSpent(b => b + spentNow);
                            setMessages(m => [...m, {
                              role: ROLES.agent,
                              text: `✅ Automatic buy orders generated · €${spentNow} allocated`,
                              ts: Date.now(),
                            }]);
                          }
                        }}
                      />
                    ))}
                  </SectionContainer>
                  <SectionContainer>
                    <SectionTitle $color="#fdba74">SELLER ACTIONS</SectionTitle>
                    {wallet && <AIWalletCard available={wallet.available} risk={wallet.risk} />}
                    {weeklyBudget !== null && (
                      <BudgetCard>
                        <BudgetTitle>REORDER BUDGET</BudgetTitle>
                        <BudgetRow>
                          <span>Total</span>
                          <span>€{weeklyBudget.toLocaleString()}</span>
                        </BudgetRow>
                        <BudgetRow>
                          <span>Spent</span>
                          <span>€{budgetSpent.toLocaleString()}</span>
                        </BudgetRow>
                        <BudgetRow $highlight>
                          <span>Remaining</span>
                          <span>€{getBudgetResidual()?.toLocaleString()}</span>
                        </BudgetRow>
                      </BudgetCard>
                    )}
                    <AISellerActions
                      aggregated={buyerAggregated}
                      activeAuctions={activeAuctions}
                      onCreateAuction={(product) => {
                        setSellerAlerts(a => [`Auction started · ${product}`, ...a]);
                        setActiveAuctions((a) => ({ ...a, [product]: { startedAt: Date.now() } }));
                        setSellerFlowAt(Date.now());
                        setMessages((m) => [...m, { role: ROLES.agent, text: `🚀 Auction started · ${product}`, ts: Date.now() }]);
                      }}
                    />
                  </SectionContainer>
                </FullScreenGrid>
              ) : (
                <>
                  <CardsContainer>
                    {aiCards.map((c, i) => c.type === "card" && (
                      <AIInsightCard
                        key={i}
                        {...c}
                        onCTA={async (action) => {
                          if (action === "confirm-budget" && weeklyBudget) {
                            let spentNow = 0;
                            const products = Object.entries(buyerAggregated);
                            let remaining = weeklyBudget;

                            for (const [product, qty] of products) {
                              if (remaining <= 0) break;
                              const buyQty = Math.min(qty, Math.floor(remaining / 20));
                              if (buyQty <= 0) continue;

                              setMemory(m => ({
                                ...m,
                                desired: [...m.desired, { product, qty: buyQty }]
                              }));

                              await persistAutoBuyToDB(product, buyQty, "weekly-budget");
                              spentNow += buyQty * 20;
                              remaining -= buyQty * 20;
                            }

                            setBudgetSpent(b => b + spentNow);
                            setMessages(m => [...m, {
                              role: ROLES.agent,
                              text: `✅ Automatic buy orders generated · €${spentNow} allocated`,
                              ts: Date.now(),
                            }]);
                          }
                        }}
                      />
                    ))}
                  </CardsContainer>
                  <CardsContainer style={{ marginTop: '0.75rem' }}>
                    {wallet && <AIWalletCard available={wallet.available} risk={wallet.risk} />}
                    {weeklyBudget !== null && (
                      <BudgetCard>
                        <BudgetTitle>REORDER BUDGET</BudgetTitle>
                        <BudgetRow>
                          <span>Total</span>
                          <span>€{weeklyBudget.toLocaleString()}</span>
                        </BudgetRow>
                        <BudgetRow>
                          <span>Spent</span>
                          <span>€{budgetSpent.toLocaleString()}</span>
                        </BudgetRow>
                        <BudgetRow $highlight>
                          <span>Remaining</span>
                          <span>€{getBudgetResidual()?.toLocaleString()}</span>
                        </BudgetRow>
                      </BudgetCard>
                    )}
                    <AIBuyerCard aggregated={buyerAggregated} />
                    <AISellerActions
                      aggregated={buyerAggregated}
                      activeAuctions={activeAuctions}
                      onCreateAuction={(product) => {
                        setActiveAuctions((a) => ({ ...a, [product]: { startedAt: Date.now() } }));
                        setSellerFlowAt(Date.now());
                        setMessages((m) => [...m, { role: ROLES.agent, text: `🚀 Auction started · ${product}`, ts: Date.now() }]);
                      }}
                    />
                    <BulkDemandMatrix aggregated={buyerAggregated} />
                  </CardsContainer>
                </>
              )}
            </ContentContainer>

            <InputContainer>
              <TextArea
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    route(input);
                    setInput("");
                  }
                }}
                placeholder="type /help (Enter to send, Shift+Enter for new line)"
              />
            </InputContainer>
          </AsideShell>
        </Overlay>
      )}
    </>
  );
}
